/**
 * Живой слой карточек (ADR 0014, D2): occupants.json отдаётся сервером ОТДЕЛЬНО
 * от venue.json и грузится ПОСЛЕ карты, не блокируя её. Данные мержатся в UI по
 * unit_id поверх статических unit.occupants; при недоступности сервера карточки
 * молча живут на статике из venue.json (оффлайн-фолбэк). Кеш — SW
 * StaleWhileRevalidate (vite.config) + серверный ETag/max-age=300.
 */
import type { LocalizedText } from '@app/rvf'
import { liveOccupants, venueMap } from './state'

/** Надмножество IMDF Occupant (схема — server/schema.sql). */
export interface LiveOccupant {
  readonly unit_id: string
  readonly name?: LocalizedText
  readonly category?: string
  readonly hours?: string
  readonly phone?: string
  readonly website?: string
  readonly description?: LocalizedText
  readonly offer_tags?: readonly string[]
  readonly photos?: readonly string[]
  /**
   * Оплаченное выделение (ADR 0019). Приходит тем же ответом, что и карточки:
   * лишний запрос на пути QR→карта оплачивался бы временем посетителя.
   * Показывать напрямую отсюда НЕЛЬЗЯ — только через promo.ts, где проверяются
   * срок и состояние экрана.
   */
  readonly emphasis?: import('./promo').PromoEmphasis
}

interface OccupantsFile {
  readonly updated?: string
  readonly occupants?: readonly LiveOccupant[]
}

/** Грузит живой слой площадки; любая ошибка — молча остаёмся на статике. */
export async function loadOccupants(venueId: string): Promise<void> {
  try {
    const response = await fetch(`/v/${encodeURIComponent(venueId)}/occupants.json`)
    if (!response.ok) return
    const parsed = (await response.json()) as OccupantsFile
    if (!Array.isArray(parsed.occupants)) return
    const map = new Map<string, LiveOccupant>()
    for (const occupant of parsed.occupants) {
      if (typeof occupant?.unit_id === 'string') map.set(occupant.unit_id, occupant)
    }
    liveOccupants.value = map
    // Карточки приезжают ПОСЛЕ карты, а имена в них сильнее имён из карты
    // (live-card.ts) — значит подписи на полигонах нужно перерисовать,
    // иначе до первого переключения языка на карте висят старые имена.
    venueMap.value?.refreshLabels()
  } catch {
    // Оффлайн/сбой сервера: карточки работают на статических occupants.
  }
}
